import { useTranslation } from 'react-i18next';
import { Link } from 'src/components/Link';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { Identifier } from 'src/types/utilityTypes';

interface PhoneConsentTextProps {
  /**
   * The ID of the account owner for this phone number.
   * Used to determine whether to show user opt-in or trainer opt-in text.
   */
  userId?: Identifier | null;
  /**
   * Forces the trainer opt-in copy to be shown regardless of userId.
   */
  isTrainer?: boolean;
  /**
   * Shows "Consent is not a condition of purchase." text.
   * Only applies to user opt-in text.
   */
  showPurchaseConsent?: boolean;
}

/**
 * Displays the appropriate phone number consent text based on context.
 *
 * For users entering their own phone number: Shows user opt-in text with
 * links to terms of service and privacy policy.
 *
 * For trainers entering a client's phone number: Shows trainer opt-in text
 * acknowledging permission to send SMS messages.
 */
export const PhoneConsentText = ({
  userId,
  isTrainer = false,
  showPurchaseConsent = false,
}: PhoneConsentTextProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'phoneConsent',
  });
  const { user, session } = useSession();

  const brand = session.brand || 'Exercise.com';

  if (!session.smsConsentCheckboxVisible) {
    return null;
  }

  // Determine if this is the current user opting in
  // Show user opt-in text if:
  // - Not explicitly set as trainer
  // - AND (no logged in user OR no userId provided OR userId matches current user)
  const isCurrentUserOptIn =
    !isTrainer && (!user?.id || !userId || userId === user.id);

  if (isCurrentUserOptIn) {
    return (
      <Typography variant="caption" color="textSecondary">
        {t('userOptIn', { brand })}{' '}
        {showPurchaseConsent && <>{t('purchaseConsent')} </>}
        {t('seeOur')}{' '}
        <Link
          href="/terms-of-service"
          target="_blank"
          color="primary"
          sx={{ typography: 'caption' }}
        >
          {t('termsOfService')}
        </Link>{' '}
        {t('and')}{' '}
        <Link
          href="/privacy-policy"
          target="_blank"
          color="primary"
          sx={{ typography: 'caption' }}
        >
          {t('privacyPolicy')}
        </Link>
        .
      </Typography>
    );
  }

  return (
    <Typography variant="caption" color="textSecondary">
      {t('trainerOptIn')}
    </Typography>
  );
};
